import React, { useReducer, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';

import useSearch from '../../hooks/useSearch';
import { useTabs } from '../../hooks/useTabs';
import SSOConfig from '../../components/SSOConfig/SSOConfig';
import { useSaveSettings } from '../../hooks/useSaveSettings';
import { CATEGORIES, SUBCATEGORIES } from '../../common/categories';
import { fieldsReducer, initFieldsState, mapToSettings } from './securityReducer';

import Category from '../../components/Category/Category';
import SecurityMain from '../../components/SecurityMain/SecurityMain';
import SecurityRoles from '../../components/SecurityRoles/SecurityRoles';
import ConfirmationMessage from '../../../Controls/ConfirmationMessage';
import { httpPost } from '../../../utils';
import { HTTP_OPTIONS, USER_ENGAGEMENT_PAGE_KEYS } from '@silverfort-nodelibs/common/utils/consts';
import { filterFieldsBySystemMode } from '../../common/utils';
import SFSupport from '../../components/SFSupport/SFSupport';
import * as actions from '../../containers/Security/securityActions';

import { SECURITY_MAIN_FIELDS, SSO_FIELDS } from '../../common/fields';
import { useVisibleTabs } from '../../hooks/lockedFeatures/useVisibleTabs';

const { HTTP_PROXY, HTTPS_PROXY, PROXY_ACCOUNT } = SECURITY_MAIN_FIELDS;
const { SSO_ENABLED } = SSO_FIELDS;
const { SECURITY } = CATEGORIES;
const { SECURITY_MAIN, SSO_CONFIG, ADMIN_SECURITY_ROLES, SF_SUPPORT } = SUBCATEGORIES;

const Security = ({ saveFunc, disableSave, systemMode }) => {
    const [showRestartServerModal, setShowRestartServerModal] = useState(false);

    const settings = useSelector((state) => state.settings.settings);

    const [fields, dispatch] = useReducer(fieldsReducer, { settings }, initFieldsState);

    const visibleFields = useSearch(useVisibleTabs(fields));
    const [selectedTab, setSelectedTab] = useTabs(SECURITY, visibleFields);
    const [save, discard] = useSaveSettings(saveFunc, settings, mapToSettings, fields, selectedTab, dispatch);
    const [refreshingProxyData, setRefreshingProxyData] = useState(false);
    const [isProxyDataUpdated, setIsProxyDataUpdated] = useState(
        systemMode === SYSTEM_MODES.ON_PREM ? settings.proxyDataUpdated : true
    );

    const saveAdminConsoleSettings = useCallback(async () => {
        const savedSettings = await save();
        if (savedSettings.showTowerRestartPopup) {
            setShowRestartServerModal(true);
        }
    }, [save]);

    const onRestartServerApproved = useCallback(async (callback) => {
        try {
            const res = await httpPost('/Settings/reconfigureProxy', null, { [HTTP_OPTIONS.HANDLE_ERRORS]: true });
            if (res.error) {
                if (res.status === 502) {
                    window.location.reload();
                } else {
                    callback(false, 'Error occurred.');
                }
            }
        } catch (err) {
            console.error(err);
        }
    }, []);

    let content = null;
    if (visibleFields[SECURITY_MAIN.ID] && selectedTab === SECURITY_MAIN.ID) {
        content = (
            <SecurityMain
                fields={filterFieldsBySystemMode(visibleFields[SECURITY_MAIN.ID].fields, systemMode)}
                dispatch={dispatch}
                isProxyDataUpdated={isProxyDataUpdated}
            />
        );
    } else if (visibleFields[ADMIN_SECURITY_ROLES.ID] && selectedTab === ADMIN_SECURITY_ROLES.ID) {
        const ssoEnabled = fields[SSO_CONFIG.ID]?.fields?.[SSO_ENABLED.ID]?.value ?? false;
        content = (
            <SecurityRoles
                fields={visibleFields[ADMIN_SECURITY_ROLES.ID].fields}
                dispatch={dispatch}
                ssoEnabled={ssoEnabled}
            />
        );
    } else if (visibleFields[SSO_CONFIG.ID] && selectedTab === SSO_CONFIG.ID) {
        content = <SSOConfig fields={visibleFields[SSO_CONFIG.ID].fields} dispatch={dispatch} />;
    } else if (visibleFields[SF_SUPPORT.ID] && selectedTab === SF_SUPPORT.ID) {
        content = <SFSupport fields={visibleFields[SF_SUPPORT.ID].fields} dispatch={dispatch} />;
    }

    if (showRestartServerModal) {
        content = (
            <>
                {content}
                <ConfirmationMessage
                    title={'Server restart'}
                    body={`The Admin Console VM must reboot (in order for the proxy configuration to complete). This could take 15 min or more. It is recommended to perform this reboot during a maintenance window.`}
                    approveText={'OK'}
                    cancelText={'Cancel'}
                    OnCancel={() => setShowRestartServerModal(false)}
                    OnApprove={onRestartServerApproved}
                />
            </>
        );
    }

    useEffect(() => {
        httpPost('/data/userEngagement', { pageId: USER_ENGAGEMENT_PAGE_KEYS.SETTINGS_ADMIN_CONSOLE });
    }, []);

    const refreshProxyData = useCallback(async () => {
        setRefreshingProxyData(true);
        try {
            const { data } = await httpPost('/Settings/RefreshProxyData');
            const proxyData = {
                [HTTP_PROXY.ID]: data.proxyData.HTTPProxy,
                [HTTPS_PROXY.ID]: data.proxyData.HTTPSProxy,
                [PROXY_ACCOUNT.ID]: { user: data.proxyData.ProxyUsername, pass: '' }
            };

            dispatch({
                type: actions.UPDATE_PROXY_DATA,
                payload: { proxyData }
            });
            setIsProxyDataUpdated(true);
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 500) {
                console.error(err.response.data);
            }
        } finally {
            setRefreshingProxyData(false);
        }
    }, []);

    const bottomActions =
        systemMode === SYSTEM_MODES.ON_PREM
            ? [{ name: refreshingProxyData ? 'Refreshing...' : 'Refresh Proxy Data', clicked: refreshProxyData }]
            : null;

    return (
        <Category
            category={SECURITY}
            fields={fields}
            visibleFields={visibleFields}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            saveSettings={saveAdminConsoleSettings}
            discard={() => discard(false)}
            discardAll={() => discard(true)}
            disableSave={disableSave}
            actions={bottomActions}>
            {content}
        </Category>
    );
};

export default Security;
